import styled, { css } from 'styled-components'
import {
  H2,
  H3,
  H4,
  P,
  Span,
  Strong,
} from '../../../shared_components/Library/Typeography'
import { Box } from '../../../shared_components/UI/Box'
import { ForceFullWidth } from '../../Home/parts'
import { MaxWidth, PureBackButton } from '../../../shared_components/Page'
import { RefObject, useEffect, useState } from 'react'
import {
  ThemedButton,
  ThemedLink,
} from '../../../shared_components/ThemedButton'
import { StakingTarget } from '../types'
import { useHistory } from 'react-router-dom'
import StakingCheckout from '../StakingCheckout'
import { OutcomeAlert } from '../OutcomeAlert'
import {
  getDailyStandardPoolRewardsForStake,
  improvedAmountFromWei,
  isTgeSoon,
  useStakingContext,
} from '../../../contexts/StakingContext'
import ConnectWalletButton from '../../../shared_components/Library/Buttons/ConnectWalletButton'
import useIsLoggedIn from '../../../hooks/useIsLoggedIn'
import useNetworkId from '../../../hooks/useNetworkId'
import SwitchToBaseButton from '../../../shared_components/Library/Buttons/SwitchToBaseButton'
import { selectAccount } from '../../../modules/web3/selectors'
import { useSelector } from 'react-redux'
import {
  MAX_MONTHS,
  TokensStakeStep,
  TokensTierStakeStep,
  VaultsStakeStep,
  VaultsTierStakeStep,
} from './StakeStep'
import {
  AssetsCont,
  ButtonsBox,
  CalculateBox,
  ClaimButton,
  ClaimInput,
  Divider,
  Result,
  ResultOutcome,
  ResultsBox,
  StakeBox,
  StakeButton,
  StakeCont,
  VaultsApyBox,
} from './styles'
import routes from '../../../router/routes'
import { CheckboxItem } from '../../Account/styles'
import CustomCheckbox from '../../Account/CustomCheckbox'
import { addDays, differenceInDays } from 'date-fns'

const vaultsHowLongMultiplierMap = {
  0: 0,
  3: 1.1,
  6: 1.3,
  12: 1.5,
}
const tokensHowLongMultiplierMap = {
  0: 0,
  3: 0.5,
  6: 0.7,
  12: 1,
}
const durationHowLongMap = {
  0: 0,
  3: 90,
  6: 180,
  12: 360,
}

function Stake({
  target,
  resultsRef,
  calcInputsRef,
}: {
  target: StakingTarget
  resultsRef: RefObject<HTMLDivElement>
  calcInputsRef: RefObject<HTMLDivElement>
}) {
  const account = useSelector(selectAccount)
  const { isLoggedIn } = useIsLoggedIn()
  const { wrongNetwork } = useNetworkId()
  const {
    vaultsAvailable,
    tokensAvailable,
    tokensStakeInformation,
    tokenApy,
    calculateBonusApy,
    getEstimatedTokensPerVaultOverStake,
    calculateDailyBonusPoolTokensForVault,
  } = useStakingContext()

  const [hasAcceptedClaimTerms, setHasAcceptedClaimTerms] = useState(false)

  const [howManyVaultsToClaim, setHowManyVaultsToClaim] = useState<number>(0)
  const [howManyVaults, setHowManyVaults] = useState(0)
  useEffect(() => {
    if (vaultsAvailable) setHowManyVaults(vaultsAvailable)
  }, [vaultsAvailable])

  const [howManyTokens, setHowManyTokens] = useState(0)
  useEffect(() => {
    if (tokensAvailable) setHowManyTokens(tokensAvailable)
  }, [tokensAvailable])

  const [howManyMonths, setHowManyMonths] = useState(MAX_MONTHS)
  const months = { 0: 0, 4: 3, 8: 6, 12: 12 }[howManyMonths]
  const monthsTiers = { 3: 0, 6: 1, 12: 2 }
  const tier = monthsTiers[months]
  const maxTier = monthsTiers[MAX_MONTHS]

  const multiplier =
    target === 'vaults'
      ? vaultsHowLongMultiplierMap[months]
      : tokensHowLongMultiplierMap[months]

  const { totalStandardWeighting } = tokensStakeInformation?.[tier] ?? {}

  const totalTokensStaked = Number(totalStandardWeighting ?? 0)

  const tokensApy = tokenApy[tier]

  const maxTokensApy = tokenApy[maxTier]

  const estimatedTokensPerVaultWei = getEstimatedTokensPerVaultOverStake(tier)
  const estimatedTokensPerVault = improvedAmountFromWei(
    estimatedTokensPerVaultWei,
  )

  const dailyStandardPoolRewardsForStake = getDailyStandardPoolRewardsForStake(
    target === 'vaults'
      ? Number(
        improvedAmountFromWei(
          !!estimatedTokensPerVaultWei
            ? estimatedTokensPerVaultWei * BigInt(howManyVaults)
            : 0n,
        ),
      )
      : howManyTokens,
    target === 'vaults' ? 1 : multiplier,
    totalTokensStaked,
  )

  const maxDailyStandardPoolRewards = getDailyStandardPoolRewardsForStake(
    target === 'vaults'
      ? Number(
        improvedAmountFromWei(
          !!estimatedTokensPerVaultWei
            ? estimatedTokensPerVaultWei * BigInt(vaultsAvailable)
            : 0n,
        ),
      )
      : tokensAvailable,
    1,
    totalTokensStaked,
  )

  const dailyBonusPoolRewardsForStake =
    calculateDailyBonusPoolTokensForVault(tier)

  const maxDailyBonusPoolRewardsPerVault =
    calculateDailyBonusPoolTokensForVault(maxTier)

  const totalStandardPoolRewardsForStake =
    dailyStandardPoolRewardsForStake * durationHowLongMap[months]

  const maxTotalStandardPoolRewardsForStake =
    maxDailyStandardPoolRewards * durationHowLongMap[MAX_MONTHS]

  const maxTotalBonusPoolRewardsForStake =
    target === 'vaults'
      ? Number(
        improvedAmountFromWei(
          Number(maxDailyBonusPoolRewardsPerVault) *
          Number(vaultsAvailable) *
          durationHowLongMap[MAX_MONTHS],
        ),
      )
      : 0

  const maxRewardsForStake =
    maxTotalStandardPoolRewardsForStake + maxTotalBonusPoolRewardsForStake

  const totalBonusPoolRewardsForStake =
    target === 'vaults'
      ? Number(
        improvedAmountFromWei(
          Number(dailyBonusPoolRewardsForStake) *
          durationHowLongMap[months] *
          Number(howManyVaults),
        ),
      )
      : 0

  const totalRewardsForStake =
    totalStandardPoolRewardsForStake + totalBonusPoolRewardsForStake

  const maxEstimatedTokensForMaxVaults = Number(maxRewardsForStake)

  const maxEstimatedExtraTokens = Math.floor(
    target === 'vaults' ? maxEstimatedTokensForMaxVaults : maxRewardsForStake,
  )

  const baseApyForTier = tokenApy[tier]
  const bonusApyForTier = calculateBonusApy(tier)
  const maxBaseApy = tokenApy[maxTier]
  const maxBonusApy = calculateBonusApy(maxTier)

  const estimatedExtraTokens = Math.floor(totalRewardsForStake)

  const maxVaultsApy = maxBaseApy + maxBonusApy
  const vaultsApy = bonusApyForTier + baseApyForTier
  const maxApy = target === 'vaults' ? maxVaultsApy : maxTokensApy

  const totalApy =
    (target === 'vaults' && Number(howManyVaults) === 0) ||
      (target === 'tokens' && Number(howManyTokens)) === 0 ||
      months === 0
      ? 0
      : target === 'vaults'
        ? vaultsApy
        : tokensApy

  const estimatedTokensForClaimWei =
    estimatedTokensPerVaultWei * BigInt(howManyVaultsToClaim)
  const estimatedTokensForClaim = Number(
    improvedAmountFromWei(estimatedTokensForClaimWei),
  )

  const handleHowManyVaultsChange = (value: number) => {
    const numVal = Number(value)
    if (numVal === 0) {
      setHowManyMonths(0)
    }
    setHowManyVaults(numVal)
  }

  const handleVaultClaimInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const numVal = Number(e.target.value)
    if (numVal > vaultsAvailable) {
      setHowManyVaultsToClaim(vaultsAvailable)
      return
    }
    setHowManyVaultsToClaim(numVal || 0)
  }

  const { push } = useHistory()
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false)

  const openCheckout = () => {
    setIsCheckoutOpen(true)
  }

  const closeCheckout = () => {
    setIsCheckoutOpen(false)
  }

  const scrollToInputs = () => {
    if (calcInputsRef.current) {
      calcInputsRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const scrollToResults = () => {
    if (resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const [vaultsStep, setVaultsStep] = useState<'idle' | 'stake' | 'claim'>(
    'idle',
  )
  const toggleHasAcceptedClaimTerms = () =>
    setHasAcceptedClaimTerms((prev) => !prev)

  useEffect(() => {
    setHasAcceptedClaimTerms(false)
  }, [howManyVaults])

  return (
    <>
      <ForceFullWidth>
        <StakeCont padT={64}>
          <MaxWidth width="100%" maxWidth={1104}>
            <StakeCommunityBonus />

            <StakeBox key={account} dc igmv relative>
              <AssetsCont>
                {/* <TokenSpinner className="left-coin" />*/}
                {/* <TokenSpinner className="right-coin" />*/}
                <img
                  className="left-coin"
                  src="/zap-coin-white-transparent-bg.svg"
                  alt="zap coin logo"
                />
                <img
                  className="right-coin"
                  src="/black-white-coin.svg"
                  alt="coin"
                />
              </AssetsCont>
              <OverflowBox dc>
                {target === 'vaults' && (
                  <VaultsStepIdle setVaultsStep={setVaultsStep} />
                )}

                {target === 'vaults' && vaultsAvailable <= 0 ? (
                  <VaultsEmptyState />
                ) : (
                  <>
                    <Box fw igph padV={48} g={40} ref={calcInputsRef}>
                      {target === 'tokens' ? (
                        <TokensStakeStep
                          howManyTokens={howManyTokens}
                          setHowManyTokens={setHowManyTokens}
                        />
                      ) : (
                        <VaultsStakeStep
                          howManyVaults={howManyVaults}
                          handleHowManyVaultsChange={handleHowManyVaultsChange}
                        />
                      )}

                      <Divider
                        h="150px"
                        w="1px"
                        toHorizontalOnViewport={1000}
                      />

                      {target === 'tokens' ? (
                        <TokensTierStakeStep
                          howManyMonths={howManyMonths}
                          setHowManyMonths={setHowManyMonths}
                          howManyTokens={howManyTokens}
                          multiplier={multiplier}
                        />
                      ) : (
                        <VaultsTierStakeStep
                          howManyMonths={howManyMonths}
                          setHowManyMonths={setHowManyMonths}
                          howManyVaults={howManyVaults}
                          multiplier={multiplier}
                        />
                      )}

                      <CalculateBox padT={40}>
                        {isLoggedIn ? (
                          <ThemedButton
                            $styleType="primary"
                            w="100%"
                            onClick={scrollToResults}
                          >
                            Calculate
                          </ThemedButton>
                        ) : (
                          <ConnectWalletButton />
                        )}
                      </CalculateBox>
                    </Box>
                    <ResultsBox dc padT={48} padB={32} g={32}>
                      <StakeCommunityBonus />
                      <Box
                        igph
                        g="var(--Index-Grids-Margins, 48px)"
                        columnMobile
                      >
                        <Box dc g={24}>
                          <Box g={10}>
                            <Span s="2XL" c="text-secondary" fw="regular">
                              3.
                            </Span>
                            <H3
                              s="2XL"
                              fw="bolder"
                              c="text-primary"
                              ff="Proxima Nova"
                              uc
                            >
                              Results
                            </H3>
                          </Box>
                          {target === 'vaults' && (
                            <VaultsApyBox g={16}>
                              <Result
                                value={
                                  isTgeSoon
                                    ? 'TBC'
                                    : howManyVaults === 0 || months === 0
                                      ? 0
                                      : tokensApy
                                }
                                valueSuffix={isTgeSoon ? undefined : '%'}
                                title="Base APY"
                                description="On combined Tokens Earning Rewards"
                              />
                              <Result
                                valuePrefix={isTgeSoon ? undefined : '+'}
                                value={bonusApyForTier.toFixed(2)}
                                valueSuffix={isTgeSoon ? undefined : '%'}
                                title="Vault APY"
                                description="Your exclusive extra APY as a Vault holder"
                                onTipClick={() => push('/article/staking')}
                                highlight
                                highlightColor={
                                  isTgeSoon
                                    ? 'brandPrimary'
                                    : howManyVaults === 0 || months === 0
                                      ? 'error'
                                      : 'brandPrimary'
                                }
                              />
                            </VaultsApyBox>
                          )}
                          <Box dc g={24}>
                            {target === 'tokens' && (
                              <>
                                <P s="L" fw="regular" c="text-primary">
                                  As a token staker your rewards are calculated
                                  based on the tokens you stake and your lock
                                  period.
                                </P>
                                <P s="L" fw="regular" c="text-primary">
                                  The early unstake penalty is 15% of staked
                                  tokens if unstaked within 25% of the stake
                                  duration. From this point it decreases
                                  linearly for the remainder of the stake
                                  duration.
                                </P>
                                {(tokensAvailable <= 100 ||
                                  howManyTokens <= 100) && (
                                    <OutcomeAlert
                                      label="You need more than 100 tokens to stake"
                                      type="error"
                                    />
                                  )}
                              </>
                            )}
                            {target === 'vaults' && (
                              <>
                                <P s="L" fw="regular" c="text-primary">
                                  As a Vault staker your rewards are calculated
                                  based on the tokens you will have at the end
                                  of your lock period.
                                </P>
                                <P s="L" fw="regular" c="text-primary">
                                  For vaults you don’t stake, your tokens will
                                  be withdrawn.
                                </P>
                                <P s="L" fw="regular" c="text-primary">
                                  The early unstake penalty is 15% of staked
                                  tokens if unstaked within 25% of the stake
                                  duration. From this point it decreases
                                  linearly for the remainder of the stake
                                  duration.
                                </P>
                              </>
                            )}
                          </Box>
                        </Box>
                        <Divider
                          h="300px"
                          w="1px"
                          toHorizontalOnViewport={1000}
                        />
                        <Box dc g={20} ref={resultsRef}>
                          <ResultOutcome
                            title={
                              target === 'tokens'
                                ? 'Your APY'
                                : 'Total blended APY'
                            }
                            value={isTgeSoon ? 'TBC' : totalApy.toFixed(2)}
                            valueSuffix={isTgeSoon ? undefined : '%'}
                            maxValue={maxApy}
                            outcomeAlert={
                              !maxApy ? (
                                <></>
                              ) : isTgeSoon ? undefined : (
                                <>
                                  {totalApy === maxApy ? (
                                    <OutcomeAlert
                                      label="You're getting the best APY"
                                      type="success"
                                    />
                                  ) : (
                                    <OutcomeAlert
                                      label={`You're missing out on ${maxApy.toFixed(
                                        2,
                                      )}% APY`}
                                      type="error"
                                      onClick={scrollToInputs}
                                    />
                                  )}
                                </>
                              )
                            }
                          />
                          <ResultOutcome
                            title="Estimated Extra Tokens Earned"
                            value={
                              isTgeSoon
                                ? 'TBC'
                                : Number(estimatedExtraTokens) || 0
                            }
                            maxValue={maxEstimatedExtraTokens}
                            outcomeAlert={
                              isTgeSoon ||
                                maxEstimatedExtraTokens === 0 ? undefined : (
                                <>
                                  {estimatedExtraTokens ===
                                    maxEstimatedExtraTokens ? (
                                    <OutcomeAlert
                                      label="You're getting the max amount of extra tokens"
                                      type="success"
                                    />
                                  ) : (
                                    <OutcomeAlert
                                      label={`You're missing out on ${maxEstimatedExtraTokens.toLocaleString()} extra tokens!`}
                                      type="error"
                                      onClick={scrollToInputs}
                                    />
                                  )}
                                </>
                              )
                            }
                          />
                        </Box>
                      </Box>
                      <Box igph>
                        <Divider w="100%" h="1px" />
                      </Box>
                      {target === 'vaults' &&
                        howManyVaults === 0 &&
                        vaultsAvailable > 0 && (
                          <Box igph>
                            <Box dc g={8} mw="477px">
                              <Strong s="M" c="text-primary" fw="semibold">
                                Claim Vaults
                              </Strong>
                              <P s="L" c="text-secondary" fw="regular">
                                By claiming your unstaked Vaults you will{' '}
                                <b>not be able to</b> stake them again in the
                                future. <br />
                                <br />
                                By doing this you will also claim all tokens
                                that are owed for these vaults, currently at a
                                value of{' '}
                                {Number(
                                  estimatedTokensPerVault,
                                ).toLocaleString()}{' '}
                                $ZAP
                              </P>
                              <Box g={12} ac>
                                <ClaimInput
                                  placeholder="How many Vaults do you want to claim?"
                                  value={howManyVaultsToClaim}
                                  onChange={handleVaultClaimInputChange}
                                  onBlur={handleVaultClaimInputChange}
                                  onKeyDown={(e) =>
                                    e.key === 'Enter' && openCheckout()
                                  }
                                />
                                <Box fu aw g={7} ac>
                                  <img
                                    height={20}
                                    width={20}
                                    src="/vault-icon.svg"
                                    alt="vault icon"
                                  />
                                  <Span s="M" fw="regular" c="text-secondary">
                                    {vaultsAvailable.toString()} Vaults
                                  </Span>
                                </Box>
                              </Box>
                              <P s="L" c="text-secondary" fw="regular">
                                {estimatedTokensForClaim.toLocaleString()}{' '}
                                Estimated Tokens
                              </P>
                            </Box>
                          </Box>
                        )}
                      {target === 'vaults' &&
                        howManyVaults === 0 &&
                        vaultsAvailable > 0 && (
                          <Box igph dc g={32}>
                            <Divider w="100%" h="1px" />
                            <Box>
                              <CheckboxItem
                                onClick={toggleHasAcceptedClaimTerms}
                                isSelected={hasAcceptedClaimTerms}
                              >
                                <CustomCheckbox
                                  isSelected={hasAcceptedClaimTerms}
                                />
                                <Box style={{ maxWidth: '600px' }}>
                                  <P s="M" fw="semibold">
                                    I understand that claiming my vault makes my
                                    tokens liquid, and I will not be able to
                                    stake my vaults in the future for extra
                                    rewards
                                  </P>
                                </Box>
                              </CheckboxItem>
                            </Box>
                          </Box>
                        )}
                      <Box dc igph>
                        {!isLoggedIn ? (
                          <ConnectWalletButton />
                        ) : wrongNetwork ? (
                          <SwitchToBaseButton />
                        ) : (
                          <ButtonsBox g={10} ac>
                            {target === 'tokens' && (
                              <StakeButton
                                isTbc={isTgeSoon}
                                disabled={
                                  howManyTokens <= 100 ||
                                  tokensAvailable <= 100 ||
                                  months === 0
                                }
                                onClick={openCheckout}
                              />
                            )}
                            {target === 'vaults' && (
                              <>
                                {howManyVaults > 0 && (
                                  <StakeButton
                                    isTbc={isTgeSoon}
                                    disabled={months === 0}
                                    onClick={openCheckout}
                                  />
                                )}
                                {howManyVaults === 0 && vaultsAvailable > 0 && (
                                  <ClaimButton
                                    isTbc={isTgeSoon}
                                    onClick={openCheckout}
                                    label="Accept & Claim"
                                    disabled={!hasAcceptedClaimTerms}
                                  />
                                )}
                              </>
                            )}
                            <ThemedButton
                              className="recalc-btn"
                              $styleType="tertiary"
                              onClick={scrollToInputs}
                            >
                              Recalculate
                            </ThemedButton>
                          </ButtonsBox>
                        )}
                      </Box>
                    </ResultsBox>
                  </>
                )}
              </OverflowBox>
            </StakeBox>
          </MaxWidth>
        </StakeCont>
      </ForceFullWidth>
      <StakingCheckout
        target={target}
        type={
          target === 'tokens'
            ? 'stake'
            : howManyVaults === 0 && howManyVaultsToClaim > 0
              ? 'claim'
              : 'stake'
        }
        amount={
          target === 'tokens'
            ? howManyTokens
            : howManyVaults === 0 && howManyVaultsToClaim > 0
              ? howManyVaultsToClaim
              : howManyVaults
        }
        months={months}
        vaultsAvailable={vaultsAvailable}
        tokensAvailable={tokensAvailable}
        totalApy={totalApy}
        multiplier={multiplier}
        estimatedExtraTokens={estimatedExtraTokens}
        maxEstimatedExtraTokens={maxEstimatedExtraTokens}
        estimatedTokensPerVault={estimatedTokensPerVault}
        estimatedTokensForClaim={estimatedTokensForClaim}
        isOpen={isCheckoutOpen}
        dismiss={closeCheckout}
      />
    </>
  )
}

export default Stake

function VaultsStepIdle({
  setVaultsStep,
}: {
  setVaultsStep: (step: 'idle' | 'stake' | 'claim') => void
}) {
  const { vaultsAvailable, vaultsStaked, vaultsClaimed } = useStakingContext()
  const { isLoggedIn } = useIsLoggedIn()
  console.log({ vaultsAvailable, vaultsStaked, vaultsClaimed })

  return (
    <Box dc>
      {isLoggedIn && (
        <Box dc padV={48} igph g={16}>
          <Box dc g={8}>
            <Box g={4} ac>
              <img
                src="/vault-icon.svg"
                alt="zap vault icon"
                style={{ height: '24px' }}
              />
              <H3 s="L" fw="bolder" c="text-primary" uc ff="Proxima Nova">
                Vaults overview
              </H3>
            </Box>
            <P s="M" fw="regular" c="text-secondary">
              A summary of where your current Vaults are
            </P>
          </Box>
          <Box g={16}>
            <StatsCard
              targetType="staked"
              value={vaultsStaked ?? 0}
              label="Staked"
              stroke="brand"
            />
            <StatsCard
              targetType="claimed"
              value={vaultsClaimed ?? 0}
              label="Claimed"
              stroke="primary"
            />
            <StatsCard
              targetType="unclaimed"
              value={vaultsAvailable ?? 0}
              label="Unclaimed"
              stroke="tertiary"
            />
          </Box>
        </Box>
      )}
      {/* <Box relative>
        <VaultSummaryBgCont>
          <img src="/vault-summary-bg.png" alt="vault summary bg" />
        </VaultSummaryBgCont>
        <Box z={10} dc padT={48} padB={32} igph g={20}>
          <Box dc g={20} mw="536px">
            <H4 s="3XL" fw="bolder" c="text-primary" uc ff="Proxima Nova">
              Would you like to stake or claim?
            </H4>
            <OutcomeAlert
              description="Remember, claiming your Vault means it cannot be staked in the future."
              descriptionStyles={{ s: 'XL', c: 'text-primary' }}
              type="error"
              boxed
            />
            <Box g={10} ac>
              <ThemedButton
                $styleType="primary"
                onClick={() => setVaultsStep('stake')}
              >
                Stake
              </ThemedButton>
              <ThemedButton
                $styleType="primary"
                onClick={() => setVaultsStep('claim')}
              >
                Claim
              </ThemedButton>
            </Box>
          </Box>
        </Box>
      </Box> */}
    </Box>
  )
}

function VaultsEmptyState() {
  const { isLoggedIn } = useIsLoggedIn()

  return (
    <EmptyStateBox>
      <VaultSummaryBgCont>
        <img src="/vault-summary-bg.png" alt="vault summary bg" />
      </VaultSummaryBgCont>
      <Box z={10} dc padT={48} padB={32} igph g={20}>
        <Box dc g={20} mw="536px">
          <H4 s="3XL" fw="bolder" c="text-primary" uc ff="Proxima Nova">
            You have no vaults to stake or claim
          </H4>
          <P s="XL" fw="regular" c="text-primary">
            If you have previously staked or claimed Vaults, head to MyZAP to
            see them
          </P>
          <Box g={10} ac>
            {isLoggedIn ? (
              <ThemedLink
                $styleType="primary"
                to={routes.myzap.tokensAndVaults}
              >
                View staked and claimed Vaults
              </ThemedLink>
            ) : (
              <ConnectWalletButton />
            )}
          </Box>
        </Box>
      </Box>
    </EmptyStateBox>
  )
}

const EmptyStateBox = styled(Box)`
  overflow: hidden;
  position: relative;
`

const OverflowBox = styled(Box)`
  // this is to hide edges of empty state background
  // if it messes up with something else, make sure to fix the above
  overflow: hidden;
  border-radius: 12px;
  z-index: 2;
`

function VaultsStepStake({
  setVaultsStep,
  howManyVaults,
  handleHowManyVaultsChange,
  howManyMonths,
  setHowManyMonths,
  months,
  multiplier,
  tokensApy,
  extraVaultsApy,
  totalApy,
  maxApy,
  estimatedExtraTokens,
  maxEstimatedExtraTokens,
  openCheckout,
}: {
  setVaultsStep: (step: 'idle' | 'stake' | 'claim') => void
  howManyVaults: number
  handleHowManyVaultsChange: (value: number) => void
  howManyMonths: number
  setHowManyMonths: (value: number) => void
  months: number
  multiplier: string
  tokensApy: number
  extraVaultsApy: number
  totalApy: number
  maxApy: number
  estimatedExtraTokens: number
  maxEstimatedExtraTokens: number
  openCheckout: () => void
}) {
  const { wrongNetwork } = useNetworkId()
  const { isLoggedIn } = useIsLoggedIn()
  const { push } = useHistory()

  return (
    <>
      <Box dc igph padV={48} g={24}>
        <PureBackButton onClick={() => setVaultsStep('idle')} padT={0} />

        <Box fw g={40}>
          <VaultsStakeStep
            howManyVaults={howManyVaults}
            handleHowManyVaultsChange={handleHowManyVaultsChange}
          />

          <Divider h="150px" w="1px" toHorizontalOnViewport={1000} />

          <VaultsTierStakeStep
            howManyMonths={howManyMonths}
            setHowManyMonths={setHowManyMonths}
            howManyVaults={howManyVaults}
            multiplier={multiplier}
          />
        </Box>
      </Box>
      <ResultsBox dc padT={48} padB={32} g={32}>
        <Box igph g="var(--Index-Grids-Margins, 48px)" columnMobile>
          <Box dc g={24}>
            <Box g={10}>
              <Span s="2XL" c="text-secondary" fw="regular">
                3.
              </Span>
              <H3 s="2XL" fw="bolder" c="text-primary" ff="Proxima Nova" uc>
                Staking Results
              </H3>
            </Box>
            <VaultsApyBox g={16}>
              <Result
                value={
                  isTgeSoon
                    ? 'TBC'
                    : howManyVaults === 0 || months === 0
                      ? 0
                      : tokensApy
                }
                valueSuffix={isTgeSoon ? undefined : '%'}
                title="Base APY"
                description="On combined Tokens Earning Rewards"
              />
              <Result
                valuePrefix={isTgeSoon ? undefined : '+'}
                value={
                  isTgeSoon
                    ? 'TBC'
                    : howManyVaults === 0 || months === 0
                      ? 0
                      : extraVaultsApy
                }
                valueSuffix={isTgeSoon ? undefined : '%'}
                title="Vault APY"
                description="Your exclusive extra APY as a Vault holder"
                onTipClick={() => push('/article/staking')}
                highlight
                highlightColor={
                  isTgeSoon
                    ? 'brandPrimary'
                    : howManyVaults === 0 || months === 0
                      ? 'error'
                      : 'brandPrimary'
                }
              />
            </VaultsApyBox>
            <Box dc g={24}>
              <>
                <P s="L" fw="regular" c="text-primary">
                  As a Vault staker your rewards are calculated based on the
                  tokens you will have at the end of your lock period.
                </P>
                <P s="L" fw="regular" c="text-primary">
                  For vaults you don’t stake, your tokens will be withdrawn.
                </P>
                <P s="L" fw="regular" c="text-primary">
                  The early unstake penalty is 15% of staked tokens if unstaked
                  within 25% of the stake duration. From this point it decreases
                  linearly for the remainder of the stake duration.
                </P>
              </>
            </Box>
          </Box>
          <Divider h="300px" w="1px" toHorizontalOnViewport={1000} />
          <Box dc g={20}>
            <ResultOutcome
              title={'Your APY'}
              value={isTgeSoon ? 'TBC' : totalApy}
              valueSuffix={isTgeSoon ? undefined : '%'}
              maxValue={maxApy}
              outcomeAlert={
                isTgeSoon ? undefined : (
                  <>
                    {totalApy === maxApy ? (
                      <OutcomeAlert
                        label="You're getting the best APY"
                        type="success"
                      />
                    ) : (
                      <OutcomeAlert
                        label={`You're missing out on ${maxApy}% APY`}
                        type="error"
                      />
                    )}
                  </>
                )
              }
            />
            <ResultOutcome
              title={'Estimated Extra Tokens Earned'}
              value={isTgeSoon ? 'TBC' : Number(estimatedExtraTokens) || 0}
              maxValue={maxEstimatedExtraTokens}
              outcomeAlert={
                isTgeSoon || maxEstimatedExtraTokens === 0 ? undefined : (
                  <>
                    {estimatedExtraTokens === maxEstimatedExtraTokens ? (
                      <OutcomeAlert
                        label="You're getting the max amount of extra tokens"
                        type="success"
                      />
                    ) : (
                      <OutcomeAlert
                        label={`You're missing out on ${maxEstimatedExtraTokens.toLocaleString()} extra tokens!`}
                        type="error"
                      />
                    )}
                  </>
                )
              }
            />
          </Box>
        </Box>
        <Box igph>
          <Divider w="100%" h="1px" />
        </Box>
        <Box dc igph>
          {!isLoggedIn ? (
            <ConnectWalletButton />
          ) : wrongNetwork ? (
            <SwitchToBaseButton />
          ) : (
            <ButtonsBox g={10} ac>
              <StakeButton
                isTbc={isTgeSoon}
                disabled={months === 0}
                onClick={openCheckout}
              />
            </ButtonsBox>
          )}
        </Box>
      </ResultsBox>
    </>
  )
}

const VaultSummaryBgCont = styled(Box)`
  position: absolute;
  right: 0;
  bottom: 0;
  height: 100%;

  @media (max-width: 1000px) {
    display: none;
  }

  img {
    position: absolute;
    bottom: 0;
    right: 0;
    height: 100%;
    object-fit: cover;
  }

  --dotSize: 1px;

  background: #080808;

  ::after {
    /* Cover our element */
    content: '';
    position: absolute;
    inset: 0;
    opacity: 0.2;

    /* Dotted background */
    background-image: radial-gradient(
      circle at center,
      var(--surface-brand, #FFF00) var(--dotSize),
      transparent 0
    );
    background-size: 3px 3px;
    background-repeat: round;
    background-position: 0 0, 0.65rem 0.65rem;
    mask-image: linear-gradient(
      45deg,
      rgba(255, 255, 0, 0) 20.48%,
      #ff0 52.34%
    );

    z-index: 1;
  }

  > * {
    z-index: 2;
  }
`

const StatsContainer = styled(Box) <{ stroke: StatsStroke; bordered?: boolean }>`
  min-height: 78px;
  border-radius: var(--units-8, 8px);
  ${({ stroke, bordered }) =>
    bordered
      ? css`
          border: var(--units-1, 1px) solid ${StatsStrokeBorderColor[stroke]};
        `
      : css`
          border: var(--units-1, 1px) solid var(--stroke-tertiary, #252525);
        `}
  background: var(--states-invert-alpha-16, rgba(0, 0, 0, 0.16));
`

type StatsStroke = 'brand' | 'primary' | 'tertiary' | 'secondary'
const StatsStrokeBorderColor = {
  brand: 'var(--stroke-brand, #FF0)',
  primary: 'var(--stroke-primary, #FFF)',
  tertiary: 'var(--stroke-tertiary, #252525)',
  secondary: 'var(--stroke-secondary, #B1B1B1)',
}

export const StatsCard = ({
  targetType,
  value,
  label,
  stroke = 'primary',
}: {
  targetType: 'staked' | 'claimed' | 'unclaimed'
  value: string | number | bigint | undefined
  label: string
  stroke: StatsStroke
}) => (
  <StatsContainer padH={16} dc jc afs stroke={stroke} bordered={true}>
    <H2
      s="L"
      fw="bolder"
      uc
      c={
        stroke === 'brand'
          ? 'text-brandPrimary'
          : stroke === 'primary'
            ? 'text-primary'
            : 'text-secondary'
      }
      lh={12}
      ff="Proxima Nova"
    >
      {value?.toString()}
    </H2>
    <P
      s="S"
      c={targetType === 'unclaimed' ? 'text-secondary' : 'text-primary'}
      fw="regular"
      lh={10}
      padT={12}
    >
      {label}
    </P>
  </StatsContainer>
)

function StakeCommunityBonus() {
  const start = new Date('2024-10-30T00:00:00.000Z')
  const end = addDays(start, 90)
  const daysRemaining = differenceInDays(end, new Date())

  if (daysRemaining <= 0) return null

  return (
    <CommunityBonusBox>
      <StakeCommunityBonusCont dc c>
        <BonusTitleCont dc c>
          <H3 s="XL" fw="bolder" c="text-brandPrimary" ff="Proxima Nova" uc>
            Community Bonus!
          </H3>
        </BonusTitleCont>
        <P s="M" fw="semibold" c="text-invert" ls="-0.28px" tac>
          Over the next {daysRemaining} days, all stakers receive a 72% bonus on
          their staking rewards. This bonus is not yet calculated into the below
          APY.
        </P>
      </StakeCommunityBonusCont>
    </CommunityBonusBox>
  )
}

const CommunityBonusBox = styled(Box)`
  @media (max-width: 1000px) {
    padding-bottom: 72px;
  }
`

const StakeCommunityBonusCont = styled(Box)`
  border-radius: var(--units-8, 8px);
  border: var(--units-1, 1px) solid #080808;
  background: #fff;
  max-width: 484px;
  padding: var(--units-12, 12px);
  gap: var(--units-8, 8px);
  margin: 0 auto;
`

const BonusTitleCont = styled(Box)`
  background: #080808;
  border-radius: var(--units-4, 4px);
  padding: 14px 0;
`
